import React, { createContext, useState, useContext, useEffect } from 'react';
import { trackSignUp, trackLogin } from '../services/analytics';
import { auth, googleProvider } from '../firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import logger from '../utils/logger';
import api from '../services/api';
import LoginModal from '../components/Auth/LoginModal';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginActionMessage, setLoginActionMessage] = useState('');
  const [loginIntentCallback, setLoginIntentCallback] = useState(null);

  const ensureUserProfile = async (user) => {
    try {
      const verifyResponse = await api.post('/auth/verify');
      const verifyData = verifyResponse.data;

      // If profile doesn't exist, create it
      if (!verifyData.user.profile) {
        logger.info('User profile not found in Firestore, creating...', {
          uid: user.uid,
          email: user.email
        });

        const registerResponse = await api.post('/auth/register', {
          displayName: user.displayName || user.email.split('@')[0]
        });

        const registerData = registerResponse.data;
        logger.info('User profile created successfully', {
          uid: registerData.user.uid
        });
      } else {
        logger.info('User profile already exists', {
          uid: user.uid
        });
      }
    } catch (error) {
      console.error('Error ensuring user profile:', error);
      logger.error('Failed to ensure user profile', {
        uid: user.uid,
        errorMessage: error.message
      });
      // Don't throw - allow user to continue even if profile creation fails
    }
  };

  const loginWithGoogle = async () => {
    try {
      logger.info('Attempting Google sign-in');
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      trackLogin('google');
      logger.info('Google sign-in successful', {
        uid: result.user.uid,
        email: result.user.email
      });

      // Ensure user has a Firestore profile
      await ensureUserProfile(result.user);

      return result;
    } catch (error) {
      console.error("Google authentication error:", error);
      logger.error('Google authentication failed', {
        errorCode: error.code,
        errorMessage: error.message
      });
      throw error;
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      logger.info('Attempting email/password sign-in', { email });
      const result = await signInWithEmailAndPassword(auth, email, password);
      setUser(result.user);
      trackLogin('email');
      logger.info('Email sign-in successful', {
        uid: result.user.uid,
        email: result.user.email
      });

      // Ensure user has a Firestore profile
      await ensureUserProfile(result.user);

      return result;
    } catch (error) {
      console.error("Email authentication error:", error);
      logger.error('Email authentication failed', {
        email,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw error;
    }
  };

  const signupWithEmail = async (email, password) => {
    try {
      logger.info('Attempting email/password signup', { email });
      const result = await createUserWithEmailAndPassword(auth, email, password);
      setUser(result.user);
      trackSignUp('email');
      logger.info('Email signup successful', {
        uid: result.user.uid,
        email: result.user.email
      });

      // Ensure user has a Firestore profile (should always create for new signups)
      await ensureUserProfile(result.user);

      return result;
    } catch (error) {
      console.error("Email signup error:", error);
      logger.error('Email signup failed', {
        email,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      logger.info('Attempting password reset', { email });
      await sendPasswordResetEmail(auth, email);
      logger.info('Password reset email sent', { email });
    } catch (error) {
      logger.error('Password reset failed', {
        email,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      logger.info('Attempting sign-out', { userId: user?.uid });
      await signOut(auth);
      setUser(null);
      logger.info('Sign-out successful');
    } catch (error) {
      console.error("Logout error:", error);
      logger.error('Sign-out failed', {
        errorCode: error.code,
        errorMessage: error.message
      });
    }
  };

  // Trigger login modal with optional action message
  const requireAuth = (actionMessage, callback) => {
    if (user) {
      // Already authenticated, execute callback immediately
      if (callback) callback();
      return true;
    }

    // Not authenticated, show login modal
    setLoginActionMessage(actionMessage || 'Sign in to continue');
    setLoginIntentCallback(() => callback);
    setLoginModalOpen(true);
    return false;
  };

  const handleLoginSuccess = (authenticatedUser) => {
    // Execute the callback if one was provided, passing the user
    if (loginIntentCallback) {
      loginIntentCallback(authenticatedUser);
      setLoginIntentCallback(null);
    }
    setLoginModalOpen(false);
    setLoginActionMessage('');
  };

  const handleLoginClose = () => {
    setLoginModalOpen(false);
    setLoginActionMessage('');
    setLoginIntentCallback(null);
  };

  useEffect(() => {
    logger.info('Setting up auth state listener', {
      authApp: auth.app.name,
      authProjectId: auth.app.options.projectId
    });

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        logger.info('Auth state changed: User signed in', {
          uid: currentUser.uid,
          email: currentUser.email,
          emailVerified: currentUser.emailVerified,
          isAnonymous: currentUser.isAnonymous,
          metadata: {
            creationTime: currentUser.metadata.creationTime,
            lastSignInTime: currentUser.metadata.lastSignInTime
          }
        });

        // Get ID token result to check custom claims
        try {
          const idTokenResult = await currentUser.getIdTokenResult();
          const adminClaim = idTokenResult.claims.admin === true;
          setIsAdmin(adminClaim);

          logger.info('User custom claims checked', {
            uid: currentUser.uid,
            isAdmin: adminClaim,
            tokenLength: idTokenResult.token.length
          });
        } catch (err) {
          logger.error('Failed to get ID token result', {
            errorMessage: err.message
          });
          setIsAdmin(false);
        }

        // Ensure user has a Firestore profile
        await ensureUserProfile(currentUser);
      } else {
        logger.info('Auth state changed: User signed out');
        setIsAdmin(false);
      }
      setUser(currentUser);
      setLoading(false);
    });

    return () => {
      logger.debug('Cleaning up auth state listener');
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAdmin,
      loginWithGoogle,
      loginWithEmail,
      signupWithEmail,
      resetPassword,
      logout,
      requireAuth,
      login: loginWithGoogle
    }}>
      {children}
      <LoginModal
        isOpen={loginModalOpen}
        onClose={handleLoginClose}
        onSuccess={handleLoginSuccess}
        actionMessage={loginActionMessage}
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
